import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { dayToDayAccounts } from '../utils/accounts';
import { ensureSubject, ensureCategory } from '../utils/taxonomy';
import '../styles/forms.css';
import SearchableSelect from './SearchableSelect';
import useIsMobile from '../hooks/useIsMobile';

const CreateTransaction = ({ editMode = false, existingTransaction = null, onCancel, onSuccess, accountId }) => {
  const today = new Date().toISOString().substring(0, 10);
  const isMobile = useIsMobile();
  const transactionType = (t) => {
    if (t.type === 'Expense' || t.type === 'Income' || t.type === 'Transfer')
      return t.type;
    // A self-referencing to_account_id (to_account_id === account_id) is the
    // backend's NOT NULL default for non-transfer recurrences, not a Transfer.
    // Real serialized transfer legs are identified by transfer_id.
    else if (t.transfer_id != null || (t.to_account_id != null && t.to_account_id !== t.account_id))
      return 'Transfer';
    else if (t.amount < 0)
      return 'Expense';
    else
      return 'Income';
  };

  const [formData, setFormData] = useState({
    from_transaction_id: null,
    account_id: null,
    account_name: '',
    amount: 0,
    to_transaction_id: null,
    to_account_id: null,
    to_account_name: '',
    to_amount: 0,
    subject_id: null,
    subject_name: '',
    category_id: null,
    category_name: '',
    notes: '',
    date: today,
    is_reconciled: false,
    type: 'Expense',
  });

  const [showAmountTo, setShowAmountTo] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [subjects, setPayees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');

  const amountRef = useRef(null);
  const amountToRef = useRef(null);

  // Add this effect to handle auto-selection
  useEffect(() => {
    var currentRef = amountRef.current;
    var currentToRef = amountToRef.current;

    if (currentRef) {
      const handleFocus = (e) => {
        e.target.select();
      };

      currentRef.addEventListener('focus', handleFocus);
      if (currentToRef) {
        currentToRef.addEventListener('focus', handleFocus);
      }

      return () => {
        currentRef?.removeEventListener('focus', handleFocus);
        currentToRef?.removeEventListener('focus', handleFocus);
      };
    }
  }, []);

  useEffect(() => {
    if (!editMode && formData.type === 'Transfer' && categories.length > 0) {
      const transferCategory = categories.find(cat => cat.name === 'Transfer');
      if (transferCategory && formData.category_id !== transferCategory.id) {
        setFormData(prev => ({
          ...prev,
          category_id: transferCategory.id,
          category_name: transferCategory.name,
        }));
      }
    }
  }, [formData.type, formData.category_id, categories, editMode]);

  const userSettings = JSON.parse(localStorage.getItem('userSettings'));

  const defaultAccountId = useCallback(() => {
    if (accountId)
      return accountId;

    return userSettings?.default_account_id ? parseInt(userSettings.default_account_id, 10) : null;
  }, [accountId, userSettings])();

  const handleChange = (e) => {
    const { name, value } = e.target;
    var newValue = value;
    if (value === 'true') newValue = true;
    if (value === 'false') newValue = false;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleAmountChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value === '' ? 0 : parseFloat(value);

    var newValue = numericValue;
    if (formData['type'] !== 'Income') {
      newValue = -Math.abs(numericValue);
    }
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleAmountToChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value === '' ? 0 : parseFloat(value);
    setFormData(prev => ({ ...prev, [name]: Math.abs(numericValue) }));
  };

  const handleSelectType = (option) => {
    setFormData(prev => ({
      ...prev,
      type: option ?? 'Expense',
    }));
  };

  const handleSelectStatus = (option) => {
    setFormData(prev => ({
      ...prev,
      is_reconciled: option === 'Reconciled',
    }));
  };

  const handleSelectAccount = useCallback((option) => {
    let account_id = null;
    let account_name = '';
    let isReconciled = false;

    if (option !== null) {
      const selectedAccount = accounts.find(a => a.id === option.value);

      if (selectedAccount?.type === 'Cash') {
        isReconciled = true;
      }

      account_id = selectedAccount.id;
      account_name = selectedAccount.name;
    }

    setFormData(prev => ({
      ...prev,
      account_id: account_id,
      account_name: account_name,
      is_reconciled: isReconciled,
    }));
  }, [accounts]);

  const handleSelectToAccount = (option) => {
    setFormData(prev => ({
      ...prev,
      to_account_id: option.value,
      to_account_name: option.label,
    }));
  };

  const handleSelectSubject = async (option) => {
    if (!option) {
      setFormData(prev => ({
        ...prev,
        subject_id: null,
        subject_name: '',
      }));
      return;
    }

    if (option.isNew) {
      try {
        const ensured = await ensureSubject(option.label);
        setPayees(prev => (prev.some(s => s.id === ensured.value)
          ? prev
          : [...prev, { id: ensured.value, name: ensured.label }]));
        option = { value: ensured.value, label: ensured.label };
      } catch (err) {
        console.error('Error creating subject:', err);
        setFormData(prev => ({
          ...prev,
          subject_id: null,
          subject_name: option.label,
        }));
        return;
      }
    }

    const selectedSubject = subjects.find(subject => subject.id === option.value);

    // Automatically select the default category if it exists
    const defaultCategory = selectedSubject?.default_category_id
      ? categories.find(category => category.id === selectedSubject.default_category_id)
      : null;

    setFormData(prev => ({
      ...prev,
      subject_id: option.value,
      subject_name: option.label,
      category_id: defaultCategory ? defaultCategory.id : null,
      category_name: defaultCategory ? defaultCategory.name : '',
    }));
  };

  const handleSelectCategory = async (option) => {
    if (!option) {
      setFormData(prev => ({
        ...prev,
        category_id: null,
        category_name: '',
      }));
      return;
    }

    if (option.isNew) {
      try {
        const ensured = await ensureCategory(option.label);
        setCategories(prev => (prev.some(c => c.id === ensured.value)
          ? prev
          : [...prev, { id: ensured.value, name: ensured.label }]));
        option = { value: ensured.value, label: ensured.label };
      } catch (err) {
        console.error('Error creating category:', err);
        setFormData(prev => ({
          ...prev,
          category_id: null,
          category_name: option.label,
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      category_id: option.value,
      category_name: option.label,
    }));
  };

  const validateForm = () => {
    const selectedAccount = accounts.find(account => account.id === formData.account_id);

    // Check if the account is of type "cash" and the amount is zero
    if (selectedAccount?.type === 'Cash' && formData.amount === 0) {
      setError('Amount cannot be zero for cash accounts.');
      return false;
    }

    // Clear any previous errors
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const dataToSubmit = { ...formData };

      if (formData.type === 'Transfer' && !showAmountTo) {
        dataToSubmit.to_amount = Math.abs(formData.amount);
      }

      var id = null;
      var res = null;
      if (editMode) {
        res = await api.patch(`/transactions/${existingTransaction.id}`, dataToSubmit);
        id = existingTransaction.id;
      } else {
        res = await api.post('/transactions', dataToSubmit);
        id = res.data.id;
      }

      resetFormData();

      onSuccess?.(id);
    } catch (error) {
      setError('Failed to save transaction. ' + error.response.data.error);
      console.error('Error creating transaction:', error);
    }
  };

  const resetFormData = () => {
    setFormData({
      from_transaction_id: null,
      account_id: null,
      account_name: '',
      amount: 0,
      to_transaction_id: null,
      to_account_id: null,
      to_account_name: '',
      to_amount: 0,
      subject_id: null,
      subject_name: '',
      category_id: null,
      category_name: '',
      notes: '',
      date: today,
      is_reconciled: false,
      type: 'Expense',
    });
  }

  // Expense/Income only offer day-to-day accounts; Transfers may fund or
  // withdraw from investment accounts, so those keep the full open list.
  const accountDropdownOptions = useMemo(() => (
    formData.type === 'Transfer'
      ? accounts.map(account => ({ value: account.id, label: account.name }))
      : dayToDayAccounts(accounts).map(account => ({ value: account.id, label: account.name }))
  ), [accounts, formData.type]);

  useEffect(() => {
    if (editMode) return;
    if (existingTransaction) return;

    if (defaultAccountId && accounts.length > 0) {
      const account = accounts.find(acc => acc.id === defaultAccountId);
      if (account && accountDropdownOptions.some(opt => opt.value === account.id)) {
        handleSelectAccount({ name: 'account', value: account.id, label: account.name });
      }
    }
  }, [defaultAccountId, accounts, editMode, handleSelectAccount, existingTransaction]);

  useEffect(() => {
    if (existingTransaction) {
      const type = transactionType(existingTransaction);
      const isTransfer = type === 'Transfer';

      // For transfers, determine if this is an incoming or outgoing transfer
      let accountData = {
        account_id: existingTransaction.account_id,
        account_name: existingTransaction.account_name
      };

      let toAccountData = {
        to_account_id: existingTransaction.to_account_id,
        to_account_name: existingTransaction.to_account_name
      };

      // If this is an incoming transfer, swap the accounts.
      // The swap uses account_from_id/account_from_name which are only present
      // in serialized real transactions (serialize_transaction), not in
      // recurrence data (Recurrence.to_json). For recurrences, account_id is
      // already the source account, so no swap is needed.
      if (isTransfer && existingTransaction.amount > 0 && existingTransaction.account_from_id != null) {
        accountData = {
          account_id: existingTransaction.account_from_id,
          account_name: existingTransaction.account_from_name
        };
      }

      setFormData({
        ...existingTransaction,
        ...accountData,
        ...toAccountData,
        date: existingTransaction.date.substring(0, 10),
        type: type,
        amount: Math.abs(existingTransaction.amount)
      });

      // Preserve a differential destination amount: if the stored transfer
      // moved a different amount into the destination account than out of
      // the source, reveal the "Amount To" field so the value survives the
      // edit instead of being overwritten with the source magnitude.
      if (isTransfer) {
        const toAmount = existingTransaction.to_amount;
        setShowAmountTo(
          toAmount != null &&
          Math.abs(Number(toAmount)) !== Math.abs(Number(existingTransaction.amount))
        );
      }
    }
  }, [existingTransaction]);

  useEffect(() => {
    async function fetchData() {
      try {
        const accountsRes = await api.get('/accounts');
        const subjectsRes = await api.get('/subjects');
        const categoriesRes = await api.get('/categories');

        const filteredAccounts = accountsRes.data.filter(a => a.is_open === true);
        const sortedAccounts = filteredAccounts.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        // All open accounts are kept; dropdown visibility is filtered at render
        // time (Expense/Income hide investment accounts, Transfer shows all).

        // Sort subjects by name
        const sortedSubjects = subjectsRes.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        // Sort categories by name and replace name with full_name
        const sortedCategories = categoriesRes.data
          .map(category => ({
            ...category,
            name: category.full_name, // Replace name with full_name
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        // Set the sorted data
        setAccounts(sortedAccounts);
        setPayees(sortedSubjects);
        setCategories(sortedCategories);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (error) {
      // Scroll to error with smooth animation
      errorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest' // or 'center'/'start'
      });
    }
  }, [error]);

  const errorRef = useRef(null);

  return (
    <div className="card">
      {/* Display error message if validation fails */}
      {error && <div ref={errorRef} className="error">{error}</div>}

      <form onSubmit={handleSubmit} className="form">
        <input type="hidden" name="transactionId" value={formData.transactionId} />

        <div className="form-group">
          <div className="radio-group">
            {['Expense', 'Income', 'Transfer'].map((type) => (
              <label key={type} className="radio-option" data-tour={`type-${type.toLowerCase()}`}>
                <span className="radio-label">{type}</span>
                <input
                  type="radio"
                  name="transactionType"
                  value={type}
                  checked={formData.type === type}
                  onChange={(e) => handleSelectType(e.target.value)}
                />
                <span className="radio-custom"></span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group-row">
          <div className="form-group">
            <label>Date
              <input type="date" name="date" value={formData.date} onChange={handleChange} required />
            </label>
          </div>

          <div className="form-group">
            <label>Amount
              <input
                type="number"
                name="amount"
                ref={amountRef}
                value={Math.abs(formData.amount)}
                onChange={handleAmountChange}
                required />
            </label>
          </div>

          {formData.type === 'Transfer' && showAmountTo && (
            <div className="form-group">
              <label>Amount To
                <input
                  type="number"
                  name="to_amount"
                  ref={amountToRef}
                  value={formData.to_amount === 0 ? '' : Math.abs(formData.to_amount)}
                  onChange={handleAmountToChange}
                  required />
              </label>
            </div>
          )}
        </div>

        {formData.type === 'Transfer' && (
          <div className="form-group">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={showAmountTo}
                onChange={(e) => setShowAmountTo(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="toggle-label">Set different amount for destination account</span>
          </div>
        )}

        <div className="form-group">
          <label>{formData.type === 'Transfer' ? 'From Account' : 'Account'}</label>
          <SearchableSelect
            options={accountDropdownOptions}
            onSelect={handleSelectAccount}
            placeholder="Search account..."
            value={formData.account_id}
          />
        </div>

        {formData.type === 'Transfer' && (
          <div className="form-group">
            <label>To Account</label>
            <SearchableSelect
              options={accounts.map(account => ({ value: account.id, label: account.name }))}
              onSelect={handleSelectToAccount}
              placeholder="Search to account..."
              value={formData.to_account_id}
            />
          </div>
        )}

        {formData.type !== 'Transfer' && (
          <div className="form-group">
            <label>{formData.type === 'Expense' ? 'To' : 'From'}</label>
            <SearchableSelect
              options={subjects.map(subject => ({ value: subject.id, label: subject.name }))}
              onSelect={handleSelectSubject}
              placeholder="Search subject..."
              value={formData.subject_id}
              allowCreate={true}
            />
          </div>
        )}

        <div className="form-group">
          <label>Category</label>
          <SearchableSelect
            options={categories.map(category => ({ value: category.id, label: category.name }))}
            onSelect={handleSelectCategory}
            placeholder="Search category..."
            value={formData.category_id}
            allowCreate={true}
          />
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            Is Reconciled&nbsp;&nbsp;
            <input
              type="checkbox"
              checked={formData.is_reconciled}
              onChange={(e) => handleSelectStatus(e.target.checked ? 'Reconciled' : 'Unreconciled')}
            />
            <span className="checkbox-custom"></span>
          </label>
        </div>

        <div className="form-group">
          <label>Notes
          </label>
          <textarea rows={5} cols={isMobile ? 20 : 40} name="notes" value={formData.notes} onChange={handleChange} />
        </div>

        <div className="form-buttons">
          <button type="button" className="btn btn--secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary">
            {editMode ? 'Save Changes' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTransaction;

